// PullUp Frontend
import React, { useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { PrivyProvider, useWallets } from '@privy-io/react-auth';
import { WagmiProvider, useSetActiveWallet } from '@privy-io/wagmi';
import { base, baseSepolia } from 'wagmi/chains';
import { wagmiConfig } from './lib/wagmi';
import { ESCROW_CHAIN_ID } from './lib/contracts';
import { App } from './App';
import { useAuthStore } from './stores/auth.store';
import { initializeSocket, disconnectSocket } from './lib/socket';
import './styles/globals.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60, // 1 minute
      retry: 1,
    },
  },
});

const privyAppId = import.meta.env.VITE_PRIVY_APP_ID || '';

/**
 * Forces the Privy embedded wallet to be the active wallet for all wagmi hooks.
 * Prevents external wallets (MetaMask, Phantom, etc.) from being used for transactions.
 */
function EmbeddedWalletEnforcer({ children }: { children: React.ReactNode }) {
  const { wallets } = useWallets();
  const { setActiveWallet } = useSetActiveWallet();

  useEffect(() => {
    const embedded = wallets.find((w) => w.walletClientType === 'privy');
    if (embedded) {
      setActiveWallet(embedded);
    }
  }, [wallets, setActiveWallet]);

  return <>{children}</>;
}

/**
 * Component that manages socket connection based on auth state
 */
function SocketManager({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, token } = useAuthStore();

  useEffect(() => {
    if (isAuthenticated && token) {
      // Initialize socket connection when authenticated
      initializeSocket({
        token,
        onConnect: () => {
          console.log('[SocketManager] Socket connected');
        },
        onDisconnect: (reason) => {
          console.log('[SocketManager] Socket disconnected:', reason);
        },
        onError: (error) => {
          console.error('[SocketManager] Socket error:', error);
        },
      });
    } else {
      // Disconnect socket when not authenticated
      disconnectSocket();
    }

    return () => {
      // Cleanup on unmount
      disconnectSocket();
    };
  }, [isAuthenticated, token]);

  return <>{children}</>;
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <PrivyProvider
      appId={privyAppId}
      config={{
        appearance: {
          theme: 'dark',
          accentColor: '#7c3aed',
          logo: '/logo.svg',
        },
        loginMethods: ['email', 'wallet'],
        embeddedWallets: {
          createOnLogin: 'users-without-wallets',
        },
        defaultChain: ESCROW_CHAIN_ID === base.id ? base : baseSepolia,
        supportedChains: [base, baseSepolia],
      }}
    >
      <QueryClientProvider client={queryClient}>
        <WagmiProvider config={wagmiConfig}>
          <EmbeddedWalletEnforcer>
            <BrowserRouter>
              <SocketManager>
                <App />
              </SocketManager>
            </BrowserRouter>
          </EmbeddedWalletEnforcer>
        </WagmiProvider>
      </QueryClientProvider>
    </PrivyProvider>
  </React.StrictMode>
);
